import { AbstractApiDataSource, PageInfo } from './abstract.api-data-source';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Filter, Pagination, Sort } from '../services/abstract-api.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { isEqual } from 'lodash-es';

// The indexes of the first and the last displayed items in a scroll panel
export interface ScrollIndexes {
    firstItemIndex: number;
    lastItemIndex: number;
}

export class VirtualScrollApiDataSource<TResourceData> extends AbstractApiDataSource<TResourceData> {
    private paginationObservable$: Observable<Pagination>;
    private sortObservable$: Observable<Sort>;
    private filterObservable$: Observable<Filter>;
    private scrollSubject = new BehaviorSubject<ScrollIndexes | null>(null);
    private fetchedPages = new Set<number>();
    private isResetting = false;

    private subscriptions: Subscription[] = [];

    public get Data(): TResourceData[] {
        return this.cachedData;
    }

    set PaginationObservable(pagination: Observable<Pagination>) {
        this.paginationObservable$ = pagination;

        const paginationSubscription = this.paginationObservable$.subscribe((paginationProps: Pagination) => {
            this.Pagination = paginationProps;
        });

        this.subscriptions.push(paginationSubscription);
    }

    set SortObservable(sort: Observable<Sort>) {
        this.sortObservable$ = sort;

        const sortSubscription = this.sortObservable$.subscribe((sortProperties: Sort) => {
            this.Sort = sortProperties;
        });

        this.subscriptions.push(sortSubscription);
    }

    set FilterObservable(filter: Observable<Filter>) {
        this.filterObservable$ = filter;

        const filterSubscription = this.filterObservable$.pipe(debounceTime(200), distinctUntilChanged(isEqual)).subscribe({
            next: (value: Filter) => {
                this.Filter = value;
                this.resetData();
            },
        });

        this.subscriptions.push(filterSubscription);
    }

    public resetData(): void {
        this.isResetting = true;
        this.fetchedPages.clear();

        this.Pagination = {
            page: 1,
            limit: this.Pagination?.limit,
        };
    }

    public onScroll(firstItemIndex: number, lastItemIndex: number): void {
        this.scrollSubject.next({
            firstItemIndex: firstItemIndex,
            lastItemIndex: lastItemIndex,
        });
    }

    public connect(): Observable<TResourceData[]> {
        this.subscriptions.push(
            this.PageInfo$.subscribe({
                next: (pageInfo: PageInfo) => {
                    this.fetchedPages.add(pageInfo.currentPage);
                    this.cachedData = this.isResetting ? Array.from<TResourceData>({ length: pageInfo.totalItems }) : this.cachedData;
                    this.isResetting = false;
                },
            }),
        );

        this.subscriptions.push(
            this.scrollSubject
                .asObservable()
                .pipe(debounceTime(500))
                .subscribe({
                    next: (indexes: ScrollIndexes | null) => {
                        if (!indexes || !this.Pagination?.limit) {
                            return;
                        }

                        const firstDisplayedPage = 1 + Math.floor(indexes.firstItemIndex / this.Pagination.limit);
                        const lastDisplayedPage = 1 + Math.floor(indexes.lastItemIndex / this.Pagination.limit);

                        if (this.tryFetchingPage(firstDisplayedPage)) {
                            // the second fetch shouldn't be done at the same time with the first one, in order not to cancel it
                            setTimeout(() => {
                                this.tryFetchingPage(lastDisplayedPage);
                            }, 400);
                        } else {
                            // first fetch was not needed, so perform immediately the second fetch
                            this.tryFetchingPage(lastDisplayedPage);
                        }
                    },
                }),
        );

        return super.connect();
    }

    public disconnect(): void {
        for (const sub of this.subscriptions) {
            sub?.unsubscribe();
        }

        super.disconnect();
    }

    protected onReceiveData(data: TResourceData[], pagination: Pagination | null): TResourceData[] {
        if (!pagination) {
            return this.cachedData;
        }

        return this.replaceDataOnPage(data, pagination);
    }

    private tryFetchingPage(pageNo: number): boolean {
        if (this.fetchedPages.has(pageNo)) {
            return false;
        }

        this.Pagination = {
            page: pageNo,
            limit: this.Pagination?.limit,
        };

        return true;
    }
}
